export type PricingBillingCycle = 'monthly' | 'yearly';
export type PricingTierGroup = 'solo' | 'team';
export type PricingTierAction = 'subscription' | 'preview';
export type PricingTierLimit = number | 'unlimited';
export type PricingTierStoreLimit = PricingTierLimit | 'custom';
export type PricingTierConnectionLimit = number | 'custom';
export type PricingTierPlanCode =
  | 'SOLO_199'
  | 'SOLO_399'
  | 'BASIC'
  | 'SOLO_999'
  | 'SOLO_1299'
  | 'TEAM_1499'
  | 'TEAM_2999'
  | 'PRO'
  | 'TEAM_9999'
  | 'TEAM_49999';

export type PricingTierId =
  | 'solo-199'
  | 'solo-399'
  | 'solo-699'
  | 'solo-999'
  | 'solo-1299'
  | 'team-1499'
  | 'team-2999'
  | 'team-4999'
  | 'team-9999'
  | 'team-enterprise';

interface TierCycleValue<T> {
  monthly: T;
  yearly: T;
}

export interface EstimatedMonthlyOperations {
  min: number;
  max: number;
}

export interface PricingTierOption {
  id: PricingTierId;
  group: PricingTierGroup;
  prices: TierCycleValue<number>;
  workspaceUsers: TierCycleValue<PricingTierLimit>;
  storeConnections: TierCycleValue<PricingTierStoreLimit>;
  integrationConnections: {
    supportsSlackAndFeishu: boolean;
    otherIntegrations: PricingTierConnectionLimit;
    total: PricingTierConnectionLimit;
  };
  workUnits: number;
  estimatedMonthlyOperations: EstimatedMonthlyOperations;
  planCode: PricingTierPlanCode | null;
  action: PricingTierAction;
}

const yearlyPrice = (monthlyPrice: number): number => Math.floor(monthlyPrice * 0.9);

const soloTier = (
  id: PricingTierId,
  monthlyPrice: number,
  workUnits: number,
  estimatedMonthlyOperations: EstimatedMonthlyOperations,
  integrationConnections: PricingTierOption['integrationConnections'],
  planCode: PricingTierPlanCode | null = null,
): PricingTierOption => ({
  id,
  group: 'solo',
  prices: {
    monthly: monthlyPrice,
    yearly: yearlyPrice(monthlyPrice),
  },
  workspaceUsers: { monthly: 1, yearly: 1 },
  storeConnections: { monthly: 1, yearly: 1 },
  integrationConnections,
  workUnits,
  estimatedMonthlyOperations,
  planCode,
  action: planCode ? 'subscription' : 'preview',
});

export const SOLO_PRICING_TIERS: readonly PricingTierOption[] = [
  soloTier('solo-199', 199, 2780, { min: 80, max: 100 }, { supportsSlackAndFeishu: false, otherIntegrations: 4, total: 5 }, 'SOLO_199'),
  soloTier('solo-399', 399, 6000, { min: 100, max: 200 }, { supportsSlackAndFeishu: false, otherIntegrations: 9, total: 10 }, 'SOLO_399'),
  soloTier('solo-699', 699, 11000, { min: 200, max: 350 }, { supportsSlackAndFeishu: false, otherIntegrations: 14, total: 15 }, 'BASIC'),
  soloTier('solo-999', 999, 15000, { min: 350, max: 500 }, { supportsSlackAndFeishu: false, otherIntegrations: 19, total: 20 }, 'SOLO_999'),
  soloTier('solo-1299', 1299, 20000, { min: 500, max: 650 }, { supportsSlackAndFeishu: false, otherIntegrations: 24, total: 25 }, 'SOLO_1299'),
];

export const TEAM_PRICING_TIERS: readonly PricingTierOption[] = [
  {
    id: 'team-1499',
    group: 'team',
    prices: { monthly: 1499, yearly: 1349 },
    workspaceUsers: { monthly: 10, yearly: 10 },
    storeConnections: { monthly: 2, yearly: 2 },
    integrationConnections: { supportsSlackAndFeishu: true, otherIntegrations: 8, total: 10 },
    workUnits: 22500,
    estimatedMonthlyOperations: { min: 650, max: 750 },
    planCode: 'TEAM_1499',
    action: 'subscription',
  },
  {
    id: 'team-2999',
    group: 'team',
    prices: { monthly: 2999, yearly: 2699 },
    workspaceUsers: { monthly: 30, yearly: 30 },
    storeConnections: { monthly: 3, yearly: 3 },
    integrationConnections: { supportsSlackAndFeishu: true, otherIntegrations: 12, total: 15 },
    workUnits: 45000,
    estimatedMonthlyOperations: { min: 1200, max: 1500 },
    planCode: 'TEAM_2999',
    action: 'subscription',
  },
  {
    id: 'team-4999',
    group: 'team',
    prices: { monthly: 4999, yearly: 4499 },
    workspaceUsers: { monthly: 50, yearly: 50 },
    storeConnections: { monthly: 10, yearly: 10 },
    integrationConnections: { supportsSlackAndFeishu: true, otherIntegrations: 40, total: 50 },
    workUnits: 75000,
    estimatedMonthlyOperations: { min: 2000, max: 2500 },
    planCode: 'PRO',
    action: 'subscription',
  },
  {
    id: 'team-9999',
    group: 'team',
    prices: { monthly: 9999, yearly: yearlyPrice(9999) },
    workspaceUsers: { monthly: 100, yearly: 100 },
    storeConnections: { monthly: 30, yearly: 30 },
    integrationConnections: { supportsSlackAndFeishu: true, otherIntegrations: 120, total: 150 },
    workUnits: 150000,
    estimatedMonthlyOperations: { min: 4000, max: 5000 },
    planCode: 'TEAM_9999',
    action: 'subscription',
  },
  {
    id: 'team-enterprise',
    group: 'team',
    prices: { monthly: 49999, yearly: yearlyPrice(49999) },
    workspaceUsers: { monthly: 'unlimited', yearly: 'unlimited' },
    storeConnections: { monthly: 100, yearly: 100 },
    integrationConnections: { supportsSlackAndFeishu: true, otherIntegrations: 400, total: 500 },
    workUnits: 750000,
    estimatedMonthlyOperations: { min: 20000, max: 25000 },
    planCode: 'TEAM_49999',
    action: 'subscription',
  },
];

export const DEFAULT_SOLO_TIER_ID: PricingTierId = 'solo-699';
export const DEFAULT_TEAM_TIER_ID: PricingTierId = 'team-4999';
export const DEFAULT_UNSUBSCRIBED_SOLO_TIER_ID: PricingTierId = 'solo-199';
export const DEFAULT_UNSUBSCRIBED_TEAM_TIER_ID: PricingTierId = 'team-1499';

export const INCLUDED_PRICING_INTEGRATIONS = [
  'Shopify',
  'WooCommerce',
  'BigCommerce',
  'Magento',
  'Wix',
  'PrestaShop',
  'Meta Ads',
  'Google Ads',
  'TikTok Ads',
  'Pinterest Ads',
  'X Ads',
  'Facebook Page',
  'Instagram',
  'Judge.me',
  'Okendo',
  'Klaviyo',
  'Mailchimp',
  'Omnisend',
  'Google Analytics',
  'Google Search Console',
  'Semrush',
  'Ahrefs',
  'Slack',
  'Feishu',
  'Figma',
] as const;

export type IncludedPricingIntegration = (typeof INCLUDED_PRICING_INTEGRATIONS)[number];

// Keep Pricing brand artwork identical across the public website and Admin modal.
export const PRICING_INTEGRATION_ICON_SOURCES: Record<IncludedPricingIntegration, string> = {
  Shopify: 'https://cdn.brandfetch.io/shopify.com/icon',
  WooCommerce: 'https://cdn.brandfetch.io/woocommerce.com/icon',
  BigCommerce: 'https://cdn.brandfetch.io/bigcommerce.com/icon',
  Magento: 'https://cdn.brandfetch.io/magento.com/icon',
  Wix: 'https://cdn.brandfetch.io/wix.com/icon',
  PrestaShop: 'https://cdn.brandfetch.io/prestashop.com/icon',
  'Meta Ads': 'https://cdn.brandfetch.io/facebook.com/icon',
  'Google Ads': '/imgs/Google_Ads.jpeg',
  'TikTok Ads': 'https://cdn.brandfetch.io/tiktok.com/icon',
  'Pinterest Ads': 'https://cdn.brandfetch.io/pinterest.com/icon',
  'X Ads': 'https://cdn.brandfetch.io/x.com/icon',
  'Facebook Page': 'https://cdn.brandfetch.io/facebook.com/icon',
  Instagram: 'https://cdn.brandfetch.io/instagram.com/icon',
  'Judge.me': 'https://cdn.brandfetch.io/judge.me/icon',
  Okendo: 'https://cdn.brandfetch.io/okendo.io/icon',
  Klaviyo: 'https://cdn.brandfetch.io/klaviyo.com/icon',
  Mailchimp: 'https://cdn.brandfetch.io/mailchimp.com/icon',
  Omnisend: 'https://cdn.brandfetch.io/omnisend.com/icon',
  'Google Analytics': '/imgs/Google_Analytics.png',
  'Google Search Console': 'https://cdn.brandfetch.io/search.google.com/icon',
  Semrush: 'https://cdn.brandfetch.io/semrush.com/icon',
  Ahrefs: 'https://cdn.brandfetch.io/ahrefs.com/icon',
  Slack: 'https://cdn.brandfetch.io/slack.com/icon',
  Feishu: 'https://cdn.brandfetch.io/feishu.cn/icon',
  Figma: 'https://cdn.brandfetch.io/figma.com/icon',
};

export const getPricingIntegrationIcon = (
  integration: IncludedPricingIntegration,
): string => PRICING_INTEGRATION_ICON_SOURCES[integration];

export const getPricingTiers = (group: PricingTierGroup): readonly PricingTierOption[] => (
  group === 'solo' ? SOLO_PRICING_TIERS : TEAM_PRICING_TIERS
);

export const getPricingTier = (
  group: PricingTierGroup,
  tierId: PricingTierId,
): PricingTierOption => (
  getPricingTiers(group).find((tier) => tier.id === tierId)
  ?? getPricingTiers(group)[0]
);

export const getPricingTierPrice = (
  tier: PricingTierOption,
  billingCycle: PricingBillingCycle,
): number => tier.prices[billingCycle];

export const getPricingTierLimits = (
  tier: PricingTierOption,
  billingCycle: PricingBillingCycle,
): { workspaceUsers: PricingTierLimit; storeConnections: PricingTierStoreLimit } => ({
  workspaceUsers: tier.workspaceUsers[billingCycle],
  storeConnections: tier.storeConnections[billingCycle],
});

export const getPricingTierIntegrationConnections = (
  tier: PricingTierOption,
): PricingTierOption['integrationConnections'] => tier.integrationConnections;

export const isUnlimitedPricingTierLimit = (
  limit: PricingTierLimit | PricingTierStoreLimit,
): limit is 'unlimited' => limit === 'unlimited';

export const isCustomPricingTierLimit = (
  limit: PricingTierStoreLimit,
): limit is 'custom' => limit === 'custom';

export interface PricingComparisonLimitValue {
  value?: number | null;
  unlimited?: boolean | null;
  configured?: boolean | null;
}

export interface PricingComparisonPlan {
  planCode?: string | null;
  planFamily?: string | null;
  rank?: number | null;
  prices?: {
    monthlyAmountCents?: number | null;
    yearlyAmountCents?: number | null;
    yearlyDisplayMonthlyAmountCents?: number | null;
  } | null;
  limits?: {
    workspaceUsers?: PricingComparisonLimitValue | null;
    storeConnections?: PricingComparisonLimitValue | null;
    supportsSlackAndFeishu?: boolean | null;
    otherIntegrationConnections?: PricingComparisonLimitValue | null;
    totalIntegrationConnections?: PricingComparisonLimitValue | null;
  } | null;
  monthlyWorkUnits?: number | null;
  estimatedMonthlyOperations?: {
    min?: number | null;
    max?: number | null;
  } | null;
}

export interface PricingComparisonCatalog {
  plans?: PricingComparisonPlan[] | null;
}

export interface PricingTierCatalog {
  soloTiers: PricingTierOption[];
  teamTiers: PricingTierOption[];
  allTiers: PricingTierOption[];
}

const PRICING_TIER_ID_BY_PLAN_CODE: Partial<Record<PricingTierPlanCode, PricingTierId>> = {
  SOLO_199: 'solo-199',
  SOLO_399: 'solo-399',
  BASIC: 'solo-699',
  SOLO_999: 'solo-999',
  SOLO_1299: 'solo-1299',
  TEAM_1499: 'team-1499',
  TEAM_2999: 'team-2999',
  PRO: 'team-4999',
  TEAM_9999: 'team-9999',
  TEAM_49999: 'team-enterprise',
};

const normalizePlanAmount = (amount?: number | null): number | null => (
  typeof amount === 'number' && Number.isFinite(amount)
    ? amount
    : null
);

const normalizePlanCode = (planCode?: string | null): PricingTierPlanCode | null => {
  if (!planCode) {
    return null;
  }
  const normalized = planCode.trim().toUpperCase() as PricingTierPlanCode;
  return PRICING_TIER_ID_BY_PLAN_CODE[normalized] ? normalized : null;
};

const limitToTierValue = (
  limit?: PricingComparisonLimitValue | null,
  fallback?: PricingTierLimit,
): PricingTierLimit => {
  if (limit?.unlimited) {
    return 'unlimited';
  }
  if (typeof limit?.value === 'number' && Number.isFinite(limit.value)) {
    return limit.value;
  }
  return fallback ?? 0;
};

const storeLimitToTierValue = (
  limit?: PricingComparisonLimitValue | null,
  fallback?: PricingTierStoreLimit,
): PricingTierStoreLimit => {
  if (limit?.unlimited) {
    return 'unlimited';
  }
  if (typeof limit?.value === 'number' && Number.isFinite(limit.value)) {
    return limit.value;
  }
  return fallback ?? 0;
};

const connectionLimitToTierValue = (
  limit?: PricingComparisonLimitValue | null,
  fallback?: PricingTierConnectionLimit,
): PricingTierConnectionLimit => {
  if (typeof limit?.value === 'number' && Number.isFinite(limit.value)) {
    return limit.value;
  }
  return fallback ?? 0;
};

const planRank = (plan?: PricingComparisonPlan): number => (
  typeof plan?.rank === 'number' && Number.isFinite(plan.rank) ? plan.rank : Number.MAX_SAFE_INTEGER
);

const applyPlanComparisonOverrides = (
  tier: PricingTierOption,
  plan?: PricingComparisonPlan,
): PricingTierOption => {
  if (!plan) {
    return { ...tier };
  }

  const monthlyPrice = normalizePlanAmount(plan.prices?.monthlyAmountCents);
  const yearlyPriceOverride = normalizePlanAmount(plan.prices?.yearlyDisplayMonthlyAmountCents);
  const workspaceUsers = limitToTierValue(plan.limits?.workspaceUsers, tier.workspaceUsers.monthly);
  const storeConnections = storeLimitToTierValue(plan.limits?.storeConnections, tier.storeConnections.monthly);
  const estimatedMin = plan.estimatedMonthlyOperations?.min;
  const estimatedMax = plan.estimatedMonthlyOperations?.max;
  const estimatedMonthlyOperations = typeof estimatedMin === 'number' && typeof estimatedMax === 'number'
    ? { min: estimatedMin, max: estimatedMax }
    : tier.estimatedMonthlyOperations;

  return {
    ...tier,
    prices: {
      monthly: monthlyPrice ?? tier.prices.monthly,
      yearly: yearlyPriceOverride ?? tier.prices.yearly,
    },
    workspaceUsers: {
      monthly: workspaceUsers,
      yearly: workspaceUsers,
    },
    storeConnections: {
      monthly: storeConnections,
      yearly: storeConnections,
    },
    integrationConnections: {
      supportsSlackAndFeishu: plan.limits?.supportsSlackAndFeishu ?? tier.integrationConnections.supportsSlackAndFeishu,
      otherIntegrations: connectionLimitToTierValue(
        plan.limits?.otherIntegrationConnections,
        tier.integrationConnections.otherIntegrations,
      ),
      total: connectionLimitToTierValue(
        plan.limits?.totalIntegrationConnections,
        tier.integrationConnections.total,
      ),
    },
    workUnits: typeof plan.monthlyWorkUnits === 'number' && Number.isFinite(plan.monthlyWorkUnits)
      ? plan.monthlyWorkUnits
      : tier.workUnits,
    estimatedMonthlyOperations,
  };
};

export const buildPricingTierCatalogFromPlanComparison = (
  catalog?: PricingComparisonCatalog | null,
): PricingTierCatalog => {
  const planByCode = new Map<PricingTierPlanCode, PricingComparisonPlan>();
  for (const plan of catalog?.plans ?? []) {
    const planCode = normalizePlanCode(plan.planCode);
    if (planCode) {
      planByCode.set(planCode, plan);
    }
  }

  const applyOverrides = (tiers: readonly PricingTierOption[]): PricingTierOption[] => (
    tiers
      .map((tier) => applyPlanComparisonOverrides(
        tier,
        tier.planCode ? planByCode.get(tier.planCode) : undefined,
      ))
      .sort((left, right) => {
        const leftRank = left.planCode ? planRank(planByCode.get(left.planCode)) : Number.MAX_SAFE_INTEGER;
        const rightRank = right.planCode ? planRank(planByCode.get(right.planCode)) : Number.MAX_SAFE_INTEGER;
        return leftRank - rightRank;
      })
  );

  const soloTiers = applyOverrides(SOLO_PRICING_TIERS);
  const teamTiers = applyOverrides(TEAM_PRICING_TIERS);
  return {
    soloTiers,
    teamTiers,
    allTiers: [...soloTiers, ...teamTiers],
  };
};
